/**
 * FormulaBar.jsx
 * 
 * FIXES:
 * 1. Autocomplete click uses onMouseDown+preventDefault so input never blurs first
 * 2. Selecting autocomplete inserts "FUNCTION(" with cursor inside parens
 * 3. handleBlur keeps isEditing=true when formula starts with '=' (cell-picking mode)
 * 4. Formula argument hint shown while typing inside a function call
 */

import React, { useCallback, useRef, useState, useEffect, memo } from 'react';
import { useFormulaStore } from '../../../libraries/spreadsheet/store/spreadsheetStores';
import {
  getFormulaAutocompleteSuggestions,
  getActiveFormulaHint,
  insertOpenParen,
  replaceFormulaToken,
  FORMULA_HINTS,
} from './formulaHelpers';

const FormulaBar = memo(({
  onFormulaCommit,
  onFormulaCancel,
  activeSheetIndex,
  columns,
  isActiveCellEditable,
}) => {
  const {
    formulaBarValue,
    activeCellAddress,
    activeCellFormula,
    isEditing,
    setFormulaBarValue,
    setActiveCellAddress,
    setEditing,
  } = useFormulaStore();

  const inputRef         = useRef(null);
  const nameBoxRef       = useRef(null);
  const pendingCursorRef = useRef(null);
  const cursorPosRef     = useRef(0);

  const [nameBoxVal,  setNameBoxVal]  = useState(activeCellAddress || '');
  const [suggestions, setSuggestions] = useState([]);
  const [selectedIdx, setSelectedIdx] = useState(-1);
  const [activeHint,  setActiveHint]  = useState(null);

  useEffect(() => { setNameBoxVal(activeCellAddress || ''); }, [activeCellAddress]);

  // Recompute hint whenever formula or cursor changes
  useEffect(() => {
    if (!formulaBarValue?.startsWith('=') || !isEditing) { setActiveHint(null); return; }
    setActiveHint(getActiveFormulaHint(formulaBarValue, cursorPosRef.current));
  }, [formulaBarValue, isEditing]);

  // Apply deferred cursor after value change
  useEffect(() => {
    if (!pendingCursorRef.current) return;
    const { start, end } = pendingCursorRef.current;
    pendingCursorRef.current = null;
    requestAnimationFrame(() => {
      if (inputRef.current) {
        inputRef.current.focus();
        inputRef.current.setSelectionRange(start, end);
        cursorPosRef.current = start;
        setActiveHint(getActiveFormulaHint(
          useFormulaStore.getState().formulaBarValue, start
        ));
      }
    });
  }, [formulaBarValue]);

  const refreshCursor = useCallback(() => {
    const pos = inputRef.current?.selectionStart ?? 0;
    cursorPosRef.current = pos;
    setActiveHint(getActiveFormulaHint(
      useFormulaStore.getState().formulaBarValue, pos
    ));
  }, []);

  const closeSuggestions = useCallback(() => {
    setSuggestions([]);
    setSelectedIdx(-1);
  }, []);

  // Apply autocomplete: replace token, insert "()", cursor inside
  const applySuggestion = useCallback((suggestion) => {
    const pos    = inputRef.current?.selectionStart ?? formulaBarValue.length;
    const token  = replaceFormulaToken(formulaBarValue, suggestion, pos);
    const next   = token.value + '()';
    const cursor = token.selectionStart + 1; // inside the parens
    setFormulaBarValue(next);
    pendingCursorRef.current = { start: cursor, end: cursor };
    cursorPosRef.current = cursor;
    closeSuggestions();
    setEditing(true);
  }, [formulaBarValue, setFormulaBarValue, closeSuggestions, setEditing]);

  const handleChange = useCallback((e) => {
    if (!isActiveCellEditable) return;

    let val = e.target.value;
    const pos = e.target.selectionStart;

    // Auto-close parentheses: =SUM( → =SUM()
    if (
      val[pos - 1] === '(' &&
      !val.includes(')') &&
      /[A-Z_]\($/.test(val.slice(Math.max(0, pos - 12), pos))
    ) {
      const balanced = insertOpenParen(val, pos - 1, pos);
      val = balanced.value;
      pendingCursorRef.current = { start: balanced.selectionStart, end: balanced.selectionEnd };
    }

    setFormulaBarValue(val);
    setEditing(true);
    cursorPosRef.current = pos;
    setSuggestions(getFormulaAutocompleteSuggestions(val, pos));
    setSelectedIdx(-1);
  }, [isActiveCellEditable, setFormulaBarValue, setEditing]);

  const handleKeyDown = useCallback((e) => {
    if (suggestions.length > 0) {
      if (e.key === 'ArrowDown') { e.preventDefault(); setSelectedIdx((i) => Math.min(i + 1, suggestions.length - 1)); return; }
      if (e.key === 'ArrowUp')   { e.preventDefault(); setSelectedIdx((i) => Math.max(i - 1, -1)); return; }
      if ((e.key === 'Tab' || e.key === 'Enter') && selectedIdx >= 0) {
        e.preventDefault();
        applySuggestion(suggestions[selectedIdx]);
        return;
      }
      if (e.key === 'Escape') { e.preventDefault(); closeSuggestions(); return; }
    }

    if (e.key === 'Enter') {
      e.preventDefault();
      if (!isActiveCellEditable) return;
      closeSuggestions();
      if (onFormulaCommit && activeCellAddress) onFormulaCommit(activeCellAddress, formulaBarValue);
      setEditing(false);
      setActiveHint(null);
      inputRef.current?.blur();
      return;
    }

    if (e.key === 'Escape') {
      e.preventDefault();
      setFormulaBarValue(activeCellFormula || '');
      setEditing(false);
      setActiveHint(null);
      closeSuggestions();
      if (onFormulaCancel) onFormulaCancel();
      inputRef.current?.blur();
    }
  }, [suggestions, selectedIdx, formulaBarValue, activeCellAddress, activeCellFormula,
      onFormulaCommit, onFormulaCancel, setFormulaBarValue, setEditing,
      isActiveCellEditable, applySuggestion, closeSuggestions]);

  const handleFocus = useCallback(() => {
    if (!isActiveCellEditable) return;
    setEditing(true);
    cursorPosRef.current = inputRef.current?.selectionStart ?? 0;
  }, [isActiveCellEditable, setEditing]);

  const handleBlur = useCallback(() => {
    // Keep editing alive while formula is in progress so user can click cells
    if (useFormulaStore.getState().formulaBarValue?.startsWith('=')) return;
    setEditing(false);
    setActiveHint(null);
    closeSuggestions();
  }, [setEditing, closeSuggestions]);

  const handleNameBoxKeyDown = useCallback((e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      const val = nameBoxVal.trim().toUpperCase();
      if (val) setActiveCellAddress(val);
      inputRef.current?.focus();
    }
    if (e.key === 'Escape') {
      setNameBoxVal(activeCellAddress || '');
      inputRef.current?.focus();
    }
  }, [nameBoxVal, activeCellAddress, setActiveCellAddress]);

  const isFormulaCell   = formulaBarValue?.startsWith('=');
  const showSuggestions = suggestions.length > 0 && isEditing && isFormulaCell;
  const showHint        = isEditing && isFormulaCell && !!activeHint && !showSuggestions;

  const renderHint = (hint) => (
    <span style={{ fontFamily: "'DM Mono','Courier New',monospace", fontSize: 11 }}>
      <span style={{ color: '#7c3aed', fontWeight: 700 }}>{hint.fnName}</span>
      <span style={{ color: '#6b7280' }}>(</span>
      {hint.args.map((arg, i) => (
        <span key={i}>
          {i > 0 && <span style={{ color: '#9ca3af' }}>, </span>}
          <span style={{
            color:      i === hint.activeArgIndex ? '#dc2626' : '#374151',
            fontWeight: i === hint.activeArgIndex ? 700 : 400,
            background: i === hint.activeArgIndex ? '#fef2f2' : 'transparent',
            borderRadius: 2,
            padding:    i === hint.activeArgIndex ? '0 2px' : 0,
          }}>{arg}</span>
        </span>
      ))}
      <span style={{ color: '#6b7280' }}>)</span>
    </span>
  );

  return (
    <div style={{
      display: 'flex', flexDirection: 'column',
      background: '#fff', borderBottom: '1px solid #e2e8f0',
      borderTop: '1px solid #e2e8f0', userSelect: 'none',
      position: 'relative', zIndex: 20,
    }}>
      {/* ── Main row ── */}
      <div style={{ display: 'flex', alignItems: 'center', height: 32 }}>

        {/* Name Box */}
        <div style={{ display:'flex', alignItems:'center', width:90, height:'100%', borderRight:'1px solid #e2e8f0', flexShrink:0 }}>
          <input
            ref={nameBoxRef}
            value={nameBoxVal}
            onChange={(e) => setNameBoxVal(e.target.value.toUpperCase())}
            onKeyDown={handleNameBoxKeyDown}
            placeholder="A1"
            spellCheck={false}
            style={{ width:'100%', height:'100%', border:'none', outline:'none', textAlign:'center', fontSize:12, fontWeight:600, fontFamily:"'DM Mono','Courier New',monospace", color:'#1e3a5f', background:'transparent', cursor:'text', padding:'0 4px' }}
          />
        </div>

        {/* ƒx */}
        <div
          onClick={() => { if (isActiveCellEditable) { inputRef.current?.focus(); setEditing(true); } }}
          title={isActiveCellEditable ? 'Insert function' : 'Cell is locked'}
          style={{ display:'flex', alignItems:'center', justifyContent:'center', width:36, height:'100%', borderRight:'1px solid #e2e8f0', flexShrink:0, cursor: isActiveCellEditable ? 'pointer' : 'not-allowed', background: isEditing && isActiveCellEditable ? '#eef2ff' : 'transparent', opacity: isActiveCellEditable ? 1 : 0.5 }}
        >
          <span style={{ fontSize:12, fontWeight:700, fontFamily:"'DM Mono','Courier New',monospace", color: isFormulaCell ? '#4f46e5' : '#6b7280' }}>ƒx</span>
        </div>

        {/* Cancel / Confirm */}
        {isEditing && isActiveCellEditable && (<>
          <button onMouseDown={(e) => { e.preventDefault(); setFormulaBarValue(activeCellFormula || ''); setEditing(false); setActiveHint(null); closeSuggestions(); if (onFormulaCancel) onFormulaCancel(); }} style={iconBtn} title="Cancel (Esc)">
            <svg width="12" height="12" viewBox="0 0 12 12"><path d="M2 2l8 8M10 2L2 10" stroke="#dc2626" strokeWidth="1.5" strokeLinecap="round"/></svg>
          </button>
          <button onMouseDown={(e) => { e.preventDefault(); if (onFormulaCommit && activeCellAddress) onFormulaCommit(activeCellAddress, formulaBarValue); setEditing(false); setActiveHint(null); closeSuggestions(); }} style={iconBtn} title="Confirm (Enter)">
            <svg width="12" height="12" viewBox="0 0 12 12"><path d="M2 6l3 3 5-5" stroke="#16a34a" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
          </button>
        </>)}

        {/* Formula input */}
        <input
          ref={inputRef}
          value={formulaBarValue}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          onKeyUp={refreshCursor}
          onClick={refreshCursor}
          onSelect={refreshCursor}
          onFocus={handleFocus}
          onBlur={handleBlur}
          disabled={!isActiveCellEditable}
          placeholder={isActiveCellEditable ? 'Enter value or formula…' : 'Cell is locked'}
          spellCheck={false}
          style={{
            flex:1, height:'100%', border:'none', outline:'none', padding:'0 10px', fontSize:12,
            fontFamily: isFormulaCell ? "'DM Mono','Courier New',monospace" : "'DM Sans',sans-serif",
            color: isActiveCellEditable ? (isFormulaCell ? '#4f46e5' : '#1f2937') : '#9ca3af',
            background: !isActiveCellEditable ? '#f3f4f6' : (isEditing ? '#fafbff' : 'transparent'),
            cursor: isActiveCellEditable ? 'text' : 'not-allowed',
            transition: 'background 0.12s',
          }}
        />

        {/* Lock icon */}
        {!isActiveCellEditable && (
          <div style={{ display:'flex', alignItems:'center', justifyContent:'center', width:28, height:'100%', marginRight:8, color:'#9ca3af' }}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/>
            </svg>
          </div>
        )}

        {/* FORMULA chip */}
        {isFormulaCell && !isEditing && (
          <div style={{ marginRight:8, padding:'1px 7px', background:'#eef2ff', borderRadius:10, fontSize:10, fontWeight:700, color:'#4f46e5', letterSpacing:'0.3px', flexShrink:0 }}>FORMULA</div>
        )}

        {/* Formula ref mode indicator */}
        {isEditing && isFormulaCell && isActiveCellEditable && (
          <div title="Click or drag cells to insert references" style={{ display:'flex', alignItems:'center', justifyContent:'center', width:28, height:'100%', marginRight:4, color:'#f59e0b', cursor:'help', fontSize:16 }}>⊕</div>
        )}
      </div>

      {/* ── Argument hint (Excel-style) ── */}
      {showHint && (
        <div style={{ padding:'4px 12px', borderTop:'1px solid #fde68a', background:'#fffbeb', display:'flex', alignItems:'center', gap:16, minHeight:26 }}>
          <div style={{ flex:1, lineHeight:1.4 }}>{renderHint(activeHint)}</div>
          {activeHint.desc && (
            <span style={{ fontSize:10, color:'#78716c', fontStyle:'italic', whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis', maxWidth:280 }}>
              {activeHint.desc}
            </span>
          )}
        </div>
      )}

      {/* ── Autocomplete dropdown ── */}
      {showSuggestions && (
        <div style={{ position:'absolute', top:'100%', left:0, right:0, background:'#fff', border:'1px solid #e2e8f0', borderTop:'none', boxShadow:'0 4px 12px rgba(0,0,0,0.1)', zIndex:1000, maxHeight:240, display:'flex', flexDirection:'column' }}>
          <div style={{ overflowY:'auto', maxHeight:180 }}>
            {suggestions.map((s, i) => (
              <div
                key={s}
                onMouseDown={(e) => { e.preventDefault(); applySuggestion(s); }}
                onMouseEnter={() => setSelectedIdx(i)}
                style={{ display:'flex', alignItems:'center', justifyContent:'space-between', padding:'5px 12px', cursor:'pointer', gap:8, background: i === selectedIdx ? '#eff6ff' : 'transparent', borderLeft:`2px solid ${i === selectedIdx ? '#3b82f6' : 'transparent'}` }}
              >
                <span style={{ fontFamily:"'DM Mono','Courier New',monospace", fontSize:12, fontWeight: i === selectedIdx ? 600 : 400, color: i === selectedIdx ? '#1d4ed8' : '#374151' }}>{s}</span>
                {FORMULA_HINTS[s]?.desc && <span style={{ fontSize:10, color:'#9ca3af', whiteSpace:'nowrap' }}>{FORMULA_HINTS[s].desc}</span>}
              </div>
            ))}
          </div>
          {selectedIdx >= 0 && suggestions[selectedIdx] && FORMULA_HINTS[suggestions[selectedIdx]] && (
            <div style={{ padding:'5px 12px', borderTop:'1px solid #f1f5f9', background:'#f8fafc', fontFamily:"'DM Mono','Courier New',monospace", fontSize:11, color:'#4b5563', fontWeight:500, whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis' }}>
              {FORMULA_HINTS[suggestions[selectedIdx]].syntax}
            </div>
          )}
        </div>
      )}
    </div>
  );
});

const iconBtn = {
  display:'flex', alignItems:'center', justifyContent:'center',
  width:28, height:'100%', border:'none', borderRight:'1px solid #e2e8f0',
  background:'transparent', cursor:'pointer', flexShrink:0, padding:0,
};

export default FormulaBar;
